// The Agents page's per-card read model (#5209, epic #5200) — pure folds joining
// four independent reads (the roster, the activity aggregate, the llm_route
// preview and the org's route pins) into what one `AgentCard` renders. The model
// saves are `agent-model-save.ts` (#5210); the drawer is #5211.

import {
  AGENT_ROSTER,
  type AgentId,
  type AgentModeKey,
  type AgentModeWritableKey,
  type AgentRoster,
  agentClaimableOrigins,
  agentModeWritable,
  LlmRoutePurpose,
  type LlmRoutePurposeAny,
  modelDisplayName,
  TASK_LANES,
} from '@developerz/domain';
import { providerLabel } from '../components/KeyCard';
import { t, tPlural } from '../i18n';
import { type AgentsPreviewEntryWire, previewStalls } from './agents-preview';
import type { RouteWire } from './llmRoutes';
import { ago, clockOrDay } from './time';

/** The ten cards the grid renders, in the page's own order — `default` is the
 * separate full-width card above the grid, not a grid cell. */
export const AGENT_CARD_ORDER: readonly AgentId[] = [
  'coder',
  'reviewer',
  'ci_fixer',
  'planner',
  'scout',
  'triage',
  'screen',
  'devops',
  'gardener',
  'escalation',
];

/**
 * The route PIN purpose every agent's picker trigger reads (`GET /v1/llm-routes`,
 * org scope) — total over {@link AgentId}, so a roster addition is a compile
 * error here until someone decides its pin purpose. For the eight preview-backed
 * agents this is the SAME purpose `agents-preview.ts`'s `AGENT_PREVIEW_PURPOSE`
 * reads; Escalation, Screen and Default read their OWN purpose (`AgentId.Escalation`
 * / `.Screen` / `.Default` are, not by accident, the same strings `LlmRoutePurpose`
 * uses).
 */
export const AGENT_ROUTE_PURPOSE: Readonly<Record<AgentId, LlmRoutePurposeAny>> = {
  coder: 'general',
  devops: 'general',
  reviewer: 'review',
  ci_fixer: 'ci-triage',
  planner: 'plan',
  scout: 'scout',
  gardener: 'gardener',
  escalation: 'escalation',
  // Triage's OWN pin (#6388); unpinned, the judge reads the Coder's `general` route.
  triage: 'triage',
  // The intake screen's OWN pin (#6938); unpinned, the fast-tier model, never `default`.
  screen: 'screen',
  default: 'default',
};

/** Each agent's display-name key — total over {@link AgentId}, so a roster addition
 * is a compile error until it has a name. The card heading and the picker's
 * "Reserved for …" both read it. */
export const AGENT_NAME_KEY: Readonly<Record<AgentId, string>> = {
  coder: 'agents.name.coder',
  reviewer: 'agents.name.reviewer',
  ci_fixer: 'agents.name.ci_fixer',
  planner: 'agents.name.planner',
  scout: 'agents.name.scout',
  devops: 'agents.name.devops',
  gardener: 'agents.name.gardener',
  escalation: 'agents.name.escalation',
  triage: 'agents.name.triage',
  screen: 'agents.name.screen',
  default: 'agents.name.default',
};

/** A purpose's ORG route in the list, or undefined: the route a card's picker pins,
 * read by the Agents page and by the Fleet page's AI roles section (#6788). */
export const orgRouteFor = (routes: readonly RouteWire[], purpose: string): RouteWire | undefined =>
  routes.find((r) => r.scope === 'org' && r.purpose === purpose);

/**
 * The picker trigger's static value (Card anatomy: "The trigger shows the pin or
 * 'Automatic'"): `provider/model` when the org pin names a model, the key's own
 * label when it names a labelled key instead, else `null` for "Automatic" — the
 * caller renders that fallback through `t('agents.automatic')` so this stays a
 * pure string-or-null fold with no i18n dependency.
 */
export function pinTriggerValue(
  routes:
    | readonly Pick<RouteWire, 'scope' | 'purpose' | 'key_label' | 'provider' | 'model'>[]
    | undefined,
  purpose: string,
): string | null {
  return routePinTriggerValue(routes?.find((r) => r.scope === 'org' && r.purpose === purpose));
}

/** {@link pinTriggerValue}'s fold, over ONE already-found route (org or repo) —
 * the Advanced drawer's per-repo rows have their row directly, with no list to
 * search (#5211). */
export function routePinTriggerValue(
  route: Pick<RouteWire, 'key_label' | 'provider' | 'model'> | undefined,
): string | null {
  if (route === undefined) return null;
  if (route.key_label !== null) return route.key_label;
  if (route.provider !== null && route.model !== null) return `${route.provider}/${route.model}`;
  if (route.model !== null) return route.model;
  if (route.provider !== null) return route.provider;
  return null;
}

/** The parts a pin is SHOWN from, in {@link routePinTriggerValue}'s precedence: a key
 * label (the model, if the row pins one too, rides beside it), else a provider and a
 * model, else either half alone. Raw wire ids: the presenter makes them readable, so
 * `routePinTriggerValue` keeps returning the id that logic and screen readers use. */
export type PinDisplay = {
  readonly keyLabel: string | null;
  readonly provider: string | null;
  readonly model: string | null;
};

export function routePinDisplay(
  route: Pick<RouteWire, 'key_label' | 'provider' | 'model'> | undefined,
): PinDisplay | null {
  if (route === undefined) return null;
  if (route.key_label !== null) {
    return { keyLabel: route.key_label, provider: null, model: route.model };
  }
  if (route.model === null && route.provider === null) return null;
  return { keyLabel: null, provider: route.provider, model: route.model };
}

/** How many REPO overrides exist for a purpose — the repo chip's count (static
 * text this issue, per the card anatomy; the link to the drawer's per-repo
 * section is #5211). */
export function repoOverrideCount(
  routes: readonly Pick<RouteWire, 'scope' | 'purpose'>[] | undefined,
  purpose: string,
): number {
  return (routes ?? []).filter((r) => r.scope === 'repo' && r.purpose === purpose).length;
}

/** The wire's own name for the three Coder-only "Fine-tune by task tag" purposes
 * — the LITERAL union {@link CODER_TASK_TAG_PURPOSES} is pinned to (asserted by
 * `agents.test.ts` against the roster fold, so a roster change that widens or
 * narrows Coder's extra purposes fails there rather than silently drifting). */
export type CoderTaskTagPurpose = 'coding' | 'quick' | 'debugging-qa';

/** The three Coder-only "Fine-tune by task tag" purposes (#5211) — every
 * `LlmRoutePurposeAny` the roster assigns to Coder besides its main `general`
 * pin. Derived from `AGENT_ROSTER.purpose`, never a second hardcoded list. */
export const CODER_TASK_TAG_PURPOSES: readonly CoderTaskTagPurpose[] = (
  Object.keys(AGENT_ROSTER.purpose) as LlmRoutePurposeAny[]
).filter((p) => p !== 'general' && AGENT_ROSTER.purpose[p] === 'coder') as CoderTaskTagPurpose[];

/**
 * Which `agent_modes` row an agent's switch and tools checklist edit, or
 * `undefined` for an agent that owns no org-writable switch (Planner, Triage, Gardener,
 * Escalation, Default). Derived from `AGENT_ROSTER.mode` — never a second hardcoded
 * map. One switch, one agent: CI fixer resolves to its own `ci_fixer` key (#7390).
 *
 * Only a key the ORG scope can write counts as owned (`agentModeWritable`, #7008): the
 * roster maps every stored key, and a key no org door may set has no card switch.
 */
export function modeKeyForAgent(agent: AgentId): AgentModeWritableKey<'org'> | undefined {
  return (Object.keys(AGENT_ROSTER.mode) as AgentModeKey[]).find(
    (key): key is AgentModeWritableKey<'org'> =>
      agentModeWritable('org', key) && AGENT_ROSTER.mode[key] === agent,
  );
}

/** The agents one mode switch governs — what a ConfirmDialog names before anything pauses.
 * One switch governs one agent, its owner (`worker` → [Coder]). */
export function agentsGoverned(modeKey: AgentModeKey): readonly AgentId[] {
  return [AGENT_ROSTER.mode[modeKey]];
}

/** Every blocker-kind tone a preview-backed card can read, per the status-tone table. */
export type AgentCardTone =
  | 'pick'
  | 'keys_cooling'
  | 'all_cooled'
  | 'box_env'
  | 'review_stall'
  | 'gardener_waiting';

/**
 * Which tone a preview entry reads. `box_env` is never derived from the blocker
 * KIND (`lane_fenced` / `models_parked` / `no_pool` also cover the Reviewer,
 * whose same three kinds read as `review_stall`) — the server already tells the
 * two apart (`entry.box_env`), so this reads that flag rather than re-deriving
 * it and risking disagreeing with the resolver's own `classifyEmptyPick` (D2:
 * never resolve box-env client-side).
 */
export function agentCardTone(entry: AgentsPreviewEntryWire): AgentCardTone {
  if (entry.pick !== null) return 'pick';
  switch (entry.blocker?.kind) {
    case 'keys_cooling':
      return 'keys_cooling';
    case 'all_cooled':
      return 'all_cooled';
    case 'no_pick_in_process':
      return 'gardener_waiting';
    default:
      return entry.box_env ? 'box_env' : 'review_stall';
  }
}

/** One agent's activity, loosened to what the card actually reads. */
export type AgentActivitySlice = {
  readonly running: number | null;
  readonly last_run_iso: string | null;
  readonly window_7d: { readonly worked: number; readonly failed: number | null } | null;
  /** Whether this agent's `agent_modes` row is ON — present on the agents a
   * mode governs (`AgentActivityWire`'s own `mode_enabled`, D11). This is the
   * ONE mode-state read that reaches a member/viewer (`read:tasks`, no
   * `write:prompts`): the Advanced drawer's own agent-modes GET 403s for
   * them, so `AgentModeSwitch` falls back to this flag for its read-only
   * "disabled, but still honest" state (#5211 AC8, spec-gate note 1). */
  readonly mode_enabled?: boolean | undefined;
  /** Asks held on this agent's selection now — the triage judge only (#6499). */
  readonly held?: number | undefined;
};

/** The activity row for one agent, or `undefined` when the read has none for it
 * (a genuinely unreachable shape in prod — every roster agent seeds a row,
 * `foldAgentActivity` — but the axe sweep's `empty` transform produces it by
 * emptying the `agents` array, and the card degrades the same way it would on
 * a null-shaped row: "—"). */
export function activityFor(
  agents: readonly (AgentActivitySlice & { readonly id: AgentId })[] | undefined,
  agent: AgentId,
): AgentActivitySlice | undefined {
  return agents?.find((a) => a.id === agent);
}

/** The pool strip's inputs, loosened to what it actually reads off a `KeyRow`. */
export type PoolKeySlice = {
  readonly status: string;
  readonly cooldown_until_iso?: string | null;
};

/** Is `key` cooling RIGHT NOW ("right now" is the caller's own clock)? */
function isCoolingAt(key: PoolKeySlice, nowMs: number): boolean {
  return key.cooldown_until_iso != null && new Date(key.cooldown_until_iso).getTime() > nowMs;
}

/** The pool strip's ready-key count: `valid` AND not presently cooling.
 * `undefined` with no read yet (the caller renders its own loading or failure
 * copy, never a guessed 0).
 *
 * KNOWN GAP (#5626): a BENCHED key (`active: false`, switched off by the org)
 * that is still `valid` and not cooling counts as ready here. The wire cannot
 * tell us otherwise: `GET /v1/billing/keys` returns every org key regardless of
 * `active`, `maskKey` does not project it, and `ListedOrgKey` adds only
 * `cooldown_until_iso`; `/v1/fleet/utilization`'s `isUsableKey` knows, but is
 * gated on `read:runners`, a scope a plain member does not hold. Closing it
 * needs an API change, which #5626 rules out; the follow-up is #5807. */
export function workingKeyCount(
  keys: readonly PoolKeySlice[] | undefined,
  nowMs: number = Date.now(),
): number | undefined {
  return keys?.filter((k) => k.status === 'valid' && !isCoolingAt(k, nowMs)).length;
}

/** What the status strip's "Running now" group shows: total tasks in flight and
 * how many agents have at least one. */
export type RunningSummary = { readonly tasks: number; readonly agents: number };

/** Fold the activity read into the strip's "Running now" figures. `undefined`
 * with no read (the caller paints its loading or failure copy, never a guessed
 * zero). `running: null` means the agent keeps no per-run task record (gardener,
 * escalation, default); it is NOT running, so it adds nothing to either figure. */
export function runningSummary(
  agents: readonly { readonly running: number | null }[] | undefined,
): RunningSummary | undefined {
  if (agents === undefined) return undefined;
  let tasks = 0;
  let busy = 0;
  for (const a of agents) {
    if (a.running === null || a.running <= 0) continue;
    tasks += a.running;
    busy += 1;
  }
  return { tasks, agents: busy };
}

/** The soonest out-of-quota reset among the pool's cooling keys, or `null` when
 * none is cooling (or the read has not landed — the caller cannot tell those
 * apart from this alone, and renders the same "no reset owed" copy for both). */
export function soonestReset(
  keys: readonly PoolKeySlice[] | undefined,
  nowMs: number = Date.now(),
): string | null {
  const cooling = (keys ?? [])
    .filter((k) => isCoolingAt(k, nowMs))
    .map((k) => k.cooldown_until_iso as string)
    // ISO-8601 timestamps sort correctly as plain strings — no Date parse needed.
    .sort();
  return cooling[0] ?? null;
}

// ─── #5624: one status pill per card (D4), plain-English week stats (D5) ───

/** Meaningful-week floor for the amber rate: under this many tasks a low
 * percentage is noise, and the card says "{w} of {n} tasks succeeded" instead. */
export const AGENT_STATS_AMBER_MIN_TASKS = 10;
/** The success rate (a rounded percentage) below which a meaningful week reads amber. */
export const AGENT_STATS_AMBER_OK_PCT = 80;

/** Amber only on a week big enough to mean something AND rough: `n >= 10` and
 * `pct < 80`. `pct` is the ROUNDED percentage the card prints — the colour must
 * agree with the number on screen (79.6 prints 80 and is not amber). */
export function agentStatsRateAmber(n: number, pct: number): boolean {
  return n >= AGENT_STATS_AMBER_MIN_TASKS && pct < AGENT_STATS_AMBER_OK_PCT;
}

/** The seven pills, by the precedence the card resolves them in. */
export type AgentPillKind =
  | 'off'
  | 'not_in_use'
  | 'on_demand'
  | 'stuck'
  | 'waiting'
  | 'running'
  | 'idle';

/** `StatusPill`'s tones, restated so this pure module names no component type. */
export type AgentPillTone = 'success' | 'live' | 'warning' | 'danger' | 'neutral';

export type AgentPill = {
  readonly kind: AgentPillKind;
  readonly tone: AgentPillTone;
  readonly label: string;
};

export type AgentStatus = {
  readonly pill: AgentPill;
  /** The one explanatory line under the pill, or `null`: a plain healthy pick says nothing. */
  readonly line: string | null;
};

/**
 * Task purposes — every pin purpose an envelope routes as: the domain's own
 * `TASK_LANES`, `plan` among them since #6764 (the Planner's `plan_author` origin
 * routes as `JobClass.Plan`). The purposes `TASK_LANES` leaves out are NOT task
 * purposes: `gardener` is a janitor loop, `escalation` is a rung a coding attempt
 * climbs to, `default` is a fall-through, and the PIN-ONLY pair (the judge, #6388,
 * and the intake screen, #6938) reads a route row of its own and is never a lane an
 * envelope runs as.
 */
const TASK_PURPOSES: ReadonlySet<LlmRoutePurposeAny> = new Set(TASK_LANES);

/**
 * Does no task run as this agent? True for an agent that OWNS a task purpose
 * (so it is a task-lane agent) while no CLAIMABLE origin is bound to it
 * (`agentClaimableOrigins`, the domain's one seam, which the queue reads too).
 * Derived from the roster, never a list of names: the Planner owns `plan` and its
 * one origin, `plan_author`, is claimable by no build until #6765, so it reads
 * Not in use; bind a claimable origin to it, or unbind the Reviewer's, and the
 * answer follows with no edit here.
 */
function isNotInUse(agent: AgentId, roster: AgentRoster): boolean {
  const ownsTaskPurpose = (Object.keys(roster.purpose) as LlmRoutePurposeAny[]).some(
    (p) => TASK_PURPOSES.has(p) && roster.purpose[p] === agent,
  );
  if (!ownsTaskPurpose) return false;
  return agentClaimableOrigins(agent, roster).length === 0;
}

/** The agent no task is ever pinned to by design: the escalation rung runs only
 * when a coding attempt stalls, and so does the agent the activity fold has no
 * source for (the intake screen, #6938), which runs per brief rather than as a
 * task lane. The triage judge HAS one since #6499: its asks. */
function isOnDemand(agent: AgentId, roster: AgentRoster): boolean {
  return roster.purpose[LlmRoutePurpose.Escalation] === agent || hasNoActivitySource(agent, roster);
}

/**
 * The intake screen (#6938): it runs per brief, but `/v1/agents/activity` folds no source
 * for it, so its slice is all-null. Read honestly as "On demand" with no stats — never
 * "Idle" / "Not run yet", which would claim it never ran. The triage judge left this set
 * in #6499: the fold now reads its asks.
 */
function hasNoActivitySource(agent: AgentId, roster: AgentRoster): boolean {
  return roster.purpose[LlmRoutePurpose.Screen] === agent;
}

/** The triage judge: the one agent whose week counts decided ASKS and whose wait is held asks. */
function isTriage(agent: AgentId, roster: AgentRoster): boolean {
  return roster.purpose[LlmRoutePurpose.Triage] === agent;
}

type PreviewFallback = NonNullable<AgentsPreviewEntryWire['fallback']>;

const FALLBACK_KEY: Readonly<
  Record<PreviewFallback['reason'], { readonly until: string; readonly noTime: string }>
> = {
  cooling: {
    until: 'agents.line.fallback.cooling_until',
    noTime: 'agents.line.fallback.cooling',
  },
  credits_exhausted: {
    until: 'agents.line.fallback.credits_until',
    noTime: 'agents.line.fallback.credits',
  },
  model_parked: {
    until: 'agents.line.fallback.parked_until',
    noTime: 'agents.line.fallback.parked',
  },
  // A selection that names no working key has no instant to wait for: one copy.
  pin_unserved: {
    until: 'agents.line.fallback.unserved',
    noTime: 'agents.line.fallback.unserved',
  },
};

type PreviewWait = NonNullable<AgentsPreviewEntryWire['wait']>;

/** One key pair per TIMED wait reason — total over the wire's set minus `saturated`. */
const WAIT_KEY: Readonly<
  Record<
    Exclude<PreviewWait['reason'], 'saturated'>,
    { readonly until: string; readonly noTime: string }
  >
> = {
  cooling: { until: 'agents.line.wait.cooling_until', noTime: 'agents.line.wait.cooling' },
  credits_exhausted: {
    until: 'agents.line.wait.credits_until',
    noTime: 'agents.line.wait.credits',
  },
};

/** "Your pick X", or "Your pick" while the routes read has not answered. */
function yourPick(pinValue: string | null): string {
  return pinValue === null
    ? t('agents.line.fallback.your_pick')
    : t('agents.line.fallback.your_pick_named', { pin: pinValue });
}

/**
 * The lane WAITS: its work has not started and will not start elsewhere. Never a
 * "Right now" reading — naming a model here would name one that is not running.
 *
 * A full key says which key and, under a selection (`pinned`), that the pick is what is
 * busy; a cooling selected key says until when. `via` is the key waited on.
 */
function waitLine(wait: PreviewWait, pinned: boolean, pinValue: string | null): string {
  const via = wait.on.key_label ?? providerLabel(wait.on.provider);
  if (wait.reason === 'saturated') {
    return pinned
      ? t('agents.line.wait.saturated_named', { via, pick: yourPick(pinValue) })
      : t('agents.line.wait.saturated', { via });
  }
  const keys = WAIT_KEY[wait.reason];
  const pick = yourPick(pinValue);
  return wait.retry_at_iso === undefined
    ? t(keys.noTime, { pick })
    : t(keys.until, { pick, when: clockOrDay(wait.retry_at_iso) });
}

/**
 * "Your pick X is out of quota until <when>; using Y meanwhile" — the PIN is the
 * subject (it is what is unavailable), the retry instant only when the wire
 * carries one, and the serving model named once. `pinValue` null means the
 * routes read has not answered yet: "Your pick", never a guessed name. When the
 * fallback serves the very model the pin names (a key-level fallback), the model
 * is not printed twice — the line names the provider that serves it instead.
 */
function fallbackLine(fallback: PreviewFallback, pinValue: string | null): string {
  const pick = yourPick(pinValue);
  const { model, provider } = fallback.using;
  const sameModel = pinValue === model || pinValue?.endsWith(`/${model}`) === true;
  const using = sameModel
    ? t('agents.line.fallback.same_model_via', { provider: providerLabel(provider) })
    : modelDisplayName(model);
  const keys = FALLBACK_KEY[fallback.reason];
  return fallback.retry_at_iso === undefined
    ? t(keys.noTime, { pick, using })
    : t(keys.until, { pick, using, when: clockOrDay(fallback.retry_at_iso) });
}

/** The Not in use line: any agent a roster change leaves unbound gets the plain sentence.
 * The Planner's own variant ("Scout writes plans") went with #6764, which made it untrue. */
function notInUseLine(agent: AgentId): string {
  return t('agents.line.not_in_use', { agent: t(AGENT_NAME_KEY[agent]) });
}

/** The pill kinds a preview outcome that is not a plain pick resolves to. */
type PreviewReading = {
  readonly waits: boolean;
  readonly line: string | null;
};

/** What the preview says beyond "stuck": whether the lane waits, and its line. */
function readPreview(entry: AgentsPreviewEntryWire, pinValue: string | null): PreviewReading {
  switch (agentCardTone(entry)) {
    case 'pick': {
      // A WAIT comes first: the work has not started, on this key or any other. The
      // pick is the key it waits for, so no line may read as if it were running.
      if (entry.wait != null) {
        const pinned = entry.pick?.source === 'pin';
        return { waits: true, line: waitLine(entry.wait, pinned, pinValue) };
      }
      const fallback = entry.fallback;
      if (fallback === null) return { waits: false, line: null };
      // `model_parked` and `pin_unserved` are not waits — a key IS serving, just not
      // the pinned model or not a key the pick names — so they keep the ordinary
      // Running/Idle pill, with their line.
      const waits = fallback.reason === 'cooling' || fallback.reason === 'credits_exhausted';
      return { waits, line: fallbackLine(fallback, pinValue) };
    }
    case 'box_env':
      return { waits: false, line: t('agents.line.box_env') };
    case 'keys_cooling': {
      const retryAt = entry.blocker?.retry_at_iso;
      return {
        waits: true,
        line: retryAt
          ? t('agents.line.cooling_until', { when: clockOrDay(retryAt) })
          : t('agents.line.cooling_no_time'),
      };
    }
    case 'all_cooled':
      return { waits: false, line: t('agents.line.all_cooled') };
    case 'review_stall':
      return { waits: false, line: t('agents.line.review_stall') };
    case 'gardener_waiting':
      return { waits: true, line: t('agents.line.gardener_waiting') };
  }
}

/**
 * The one pill a card wears, and its line (D4). Pure; precedence, first match wins:
 * Off (the agent's mode is switched off — `activity.mode_enabled`, the CI fixer's
 * from its own `ci_fixer` switch) > Not in use (no claimable task origin is
 * bound to the agent) > On demand (the escalation rung) > Stuck (the rail dot's own
 * predicate, `previewStalls`) > Waiting (a cooling pool, a gardener with no
 * key, a pick serving on a cooling or out-of-credits fallback) > Running {n} >
 * Idle. An unread preview (still loading) or none for the agent falls to
 * Running/Idle: a card never goes pill-less and never guesses a stall.
 *
 * `roster` is injectable so a fixture roster can prove "Not in use" is derived.
 */
export function agentStatus(
  agent: AgentId,
  preview: AgentsPreviewEntryWire | undefined,
  activity: AgentActivitySlice | undefined,
  pinValue: string | null,
  roster: AgentRoster = AGENT_ROSTER,
): AgentStatus {
  if (activity?.mode_enabled === false) {
    return { pill: { kind: 'off', tone: 'neutral', label: t('agents.pill.off') }, line: null };
  }
  if (isNotInUse(agent, roster)) {
    return {
      pill: { kind: 'not_in_use', tone: 'neutral', label: t('agents.pill.not_in_use') },
      line: notInUseLine(agent),
    };
  }
  if (isOnDemand(agent, roster)) {
    return {
      pill: { kind: 'on_demand', tone: 'neutral', label: t('agents.pill.on_demand') },
      line: null,
    };
  }
  const reading =
    preview === undefined ? { waits: false, line: null } : readPreview(preview, pinValue);
  if (preview !== undefined && previewStalls(preview)) {
    return {
      pill: { kind: 'stuck', tone: 'danger', label: t('agents.pill.stuck') },
      line: reading.line,
    };
  }
  if (reading.waits) {
    return {
      pill: { kind: 'waiting', tone: 'warning', label: t('agents.pill.waiting') },
      line: reading.line,
    };
  }
  // The triage judge's own wait (#6499): asks held on its key or model. Said with the
  // count and the card that fixes it, never a bare "waiting".
  const held = activity?.held ?? 0;
  if (isTriage(agent, roster) && held > 0) {
    return {
      pill: { kind: 'waiting', tone: 'warning', label: t('agents.pill.waiting') },
      line: tPlural('agents.line.triage_held', held),
    };
  }
  const running = activity?.running ?? null;
  if (running !== null && running > 0) {
    return {
      pill: { kind: 'running', tone: 'live', label: t('agents.pill.running', { n: running }) },
      line: reading.line,
    };
  }
  return {
    pill: { kind: 'idle', tone: 'neutral', label: t('agents.pill.idle') },
    line: reading.line,
  };
}

/** One piece of the stats line — rendered as a span, joined by " · ". */
export type AgentStatSegment = {
  readonly text: string;
  readonly amber: boolean;
  /** Hover text, on the success rate only. */
  readonly title?: string;
};

export type AgentStats =
  | { readonly kind: 'segments'; readonly segments: readonly AgentStatSegment[] }
  | { readonly kind: 'empty' };

const plain = (text: string): AgentStatSegment => ({ text, amber: false });

/** The week segment(s) for one agent's `window_7d`, or none when it is untracked. */
/** The triage judge's week (#6499): the asks it DECIDED, whatever the verdict. */
function triageWeek(decided: number): string {
  if (decided === 0) return t('agents.activity.triage_decided_none');
  return tPlural('agents.activity.triage_decided', decided);
}

function weekSegments(window7d: NonNullable<AgentActivitySlice['window_7d']>): AgentStatSegment[] {
  const { worked, failed } = window7d;
  if (failed === null) {
    // The escalation lane's own shape: a routing decision, never a run outcome.
    if (worked === 0) return [plain(t('agents.activity.escalated_none'))];
    return [plain(tPlural('agents.activity.escalated', worked))];
  }
  const total = worked + failed;
  if (total === 0) return [plain(t('agents.activity.week_none'))];
  if (total < AGENT_STATS_AMBER_MIN_TASKS) {
    return [plain(t('agents.activity.week_few', { worked, n: total }))];
  }
  const pct = Math.round((worked / total) * 100);
  return [
    {
      text: t('agents.activity.success_rate', { pct }),
      amber: agentStatsRateAmber(total, pct),
      title: t('agents.activity.success_rate_title', { worked, failed }),
    },
    plain(t('agents.activity.week_tasks', { n: total })),
  ];
}

/**
 * The stats line, as segments (D5): `Last run … · {pct}% success rate · {n} tasks`.
 * No dashes anywhere: an agent that never ran says "Not run yet"; a week with
 * nothing in it says so; a failed activity read says "Couldn't check right now"
 * (never a blank); the not-in-use agent and a still-loading read render nothing.
 * `window_7d === null` (the Gardener) is "not tracked", so it has no week segment.
 */
export function agentStats(
  agent: AgentId,
  activity: AgentActivitySlice | undefined,
  failed: boolean,
  roster: AgentRoster = AGENT_ROSTER,
): AgentStats {
  if (isNotInUse(agent, roster) || hasNoActivitySource(agent, roster)) return { kind: 'empty' };
  if (activity === undefined) {
    return failed
      ? { kind: 'segments', segments: [plain(t('agents.line.unavailable'))] }
      : { kind: 'empty' };
  }
  const lastRun =
    activity.last_run_iso === null
      ? t('agents.activity.not_run')
      : t('agents.activity.last_run', { when: ago(activity.last_run_iso) });
  const week =
    activity.window_7d === null
      ? []
      : isTriage(agent, roster)
        ? [plain(triageWeek(activity.window_7d.worked))]
        : weekSegments(activity.window_7d);
  return { kind: 'segments', segments: [plain(lastRun), ...week] };
}
